import { CenterFlexColumn, CenterFlexRow, FlexRow } from "styles/layout";
import { convertMsToSecondsString, convertMsToTimeString } from "TimeHelper";
import { DnfIcon, PickName, TrackName } from "../styles";
import {
  selectRosterDisplayName,
  selectTournamentDriverName,
} from "selectors/Roster";
import { DNF_ICON_SRC } from "theme";
import {
  statTimesBest,
  statTimesDnfCount,
  statTimesMedian,
} from "stats/TimeStats";
import { nonUndefined } from "Utility";
import {
  DriverTr,
  DriverNameContainer,
  OrgLogoContainer,
  HeaderTr,
  TrackStatsTable,
  DnfTd,
  MedianTd,
  BestTd,
  PlayedCountTd,
  StatTimeDifferenceContainer,
  Time,
  AverageStatTimeDifference,
  BestStatTimeDifference,
} from "./styles";
import { MatchTeams } from "model/Match/MatchTeams";
import { TrackRound } from "model/Match/Track/TrackRound";
import { CupModeRound } from "model/Match/Set/CupMode/Track/CupModeRound";
import { RoundsRound } from "model/Match/Set/Rounds/Track/RoundsRound";
import { NonTAMatchTrack } from "model/Match/Track/MatchTrack";
import { getRosterDriverIds, getRosterId } from "model/Roster/Roster";
import { RosterOrgLogo } from "components/Logo/RosterOrgLogo";
import { selectTrackNameFromTournamentId } from "selectors/Tournament";
import { useAppSelector } from "app/hooks";
import { statRoundsBestCombinedTime } from "stats/RoundStats";

const DNF_ICON = <DnfIcon src={DNF_ICON_SRC} />;

type DriverTrackStats = {
  best: number | undefined;
  dnfCount: number;
  median: number | undefined;
  roundsPlayed: number;
};

interface Props {
  trackId: string;
  matchTracks: NonTAMatchTrack[] | undefined;
  teams: MatchTeams;
  pickingRosterId: string | undefined;
  tournamentId: string;
  showPickName?: boolean;
}

export const RoundsBasedTrackStats = ({
  trackId,
  matchTracks,
  teams,
  pickingRosterId,
  tournamentId,
  showPickName = true,
}: Props) => {
  const trackName = useAppSelector((state) =>
    selectTrackNameFromTournamentId(state, trackId, tournamentId)
  );

  const pickName = useAppSelector((state) => {
    if (!pickingRosterId) {
      return "Random";
    }

    return `${selectRosterDisplayName(
      state,
      pickingRosterId,
      tournamentId
    )} Pick`;
  });

  const rounds = (matchTracks ?? []).flatMap(
    (t) => t.rounds as (CupModeRound | RoundsRound)[]
  );

  const driverStats: {
    [driverId: string]: DriverTrackStats;
  } = {};

  const driverDetails = useAppSelector((state) =>
    teams.rosters.flatMap((r) =>
      (getRosterDriverIds(r, teams.scope) ?? []).map((driverId) => ({
        driverId,
        driverName: selectTournamentDriverName(state, driverId, tournamentId),
        rosterId: getRosterId(r),
      }))
    )
  );

  const driverIds = driverDetails.flatMap((d) => d.driverId);

  driverIds.forEach(
    (driverId) =>
      (driverStats[driverId] = getDriverStats(rounds ?? [], driverId))
  );

  const allDriverStats = Object.values(driverStats);
  const allBests = nonUndefined(allDriverStats.flatMap((s) => s.best));
  const overallBest = allBests.length > 0 ? Math.min(...allBests) : undefined;
  const allMedians = nonUndefined(
    allDriverStats.flatMap((s) => s.median)
  ).filter((m) => m > 0);
  const bestMedian =
    allMedians.length > 0 ? Math.min(...allMedians) : undefined;
  const roundTimes = rounds.flatMap((r) => r.times) ?? [];
  var overallMedian = statTimesMedian(roundTimes);
  if (!!overallMedian) {
    if (overallMedian < 0) {
      overallMedian = undefined;
    } else {
      overallMedian = Math.round(overallMedian);
    }
  }

  const bestRosterTime =
    tournamentId === "AmericasMastersx1"
      ? statRoundsBestCombinedTime(rounds, teams)
      : undefined;

  const sortedDriverDetails = driverDetails.sort(
    (a, b) =>
      (driverStats[a.driverId]?.best ?? Infinity) -
      (driverStats[b.driverId]?.best ?? Infinity)
  );

  const showTrackStats = rounds.length > 0;
  return (
    <CenterFlexColumn>
      <TrackName played={showTrackStats}>
        {trackName}
        {showPickName && <PickName> - {pickName}</PickName>}
      </TrackName>

      {showTrackStats && (
        <>
          {!!bestRosterTime && <>{RosterRow(bestRosterTime, tournamentId)}</>}
          <TrackStatsTable>
            <thead>
              <HeaderTr>
                <th />
                <th>Played</th>
                <th>Best</th>
                <th>Median</th>
              </HeaderTr>
            </thead>
            <tbody>
              {sortedDriverDetails.map((details) => {
                const stats = driverStats[details.driverId];
                return (
                  !!stats &&
                  stats.roundsPlayed > 0 &&
                  DriverRow(
                    details,
                    stats,
                    overallBest,
                    bestMedian,
                    overallMedian,
                    tournamentId
                  )
                );
              })}
            </tbody>
          </TrackStatsTable>
        </>
      )}
    </CenterFlexColumn>
  );
};

const getDriverStats = (
  rounds: TrackRound[],
  driverId: string
): DriverTrackStats => {
  const driverRounds = rounds.flatMap((r) =>
    r.times.filter((t) => t.driverId === driverId)
  );
  const median = statTimesMedian(driverRounds);
  const roundedMedian = median !== undefined ? Math.round(median) : undefined;
  const best = statTimesBest(driverRounds);
  const dnfCount = statTimesDnfCount(driverRounds);
  return {
    best,
    dnfCount,
    roundsPlayed: driverRounds.length,
    median: roundedMedian,
  };
};

const getDnfIcons = (dnfCount: number) => {
  if (dnfCount === 0) {
    return <></>;
  }

  return (
    <>
      {DNF_ICON}
      <span style={{ paddingLeft: "1px" }}>x</span>
      {dnfCount}
    </>
  );
};

const RosterRow = (
  bestRosterTime: { rosterId: string; bestTime: number | undefined },
  tournamentId: string
) => {
  const rosterName = useAppSelector((state) =>
    selectRosterDisplayName(state, bestRosterTime.rosterId, tournamentId)
  );

  const bestTimeString = !!bestRosterTime.bestTime
    ? convertMsToTimeString(bestRosterTime.bestTime)
    : "-";

  return (
    <CenterFlexRow style={{ padding: "5px" }}>
      <span style={{ paddingRight: "12px" }}>Team Track Record</span>
      <div style={{ width: "18px", height: "18px", paddingRight: "5px" }}>
        <RosterOrgLogo
          key={bestRosterTime.rosterId}
          rosterId={bestRosterTime.rosterId}
          tournamentId={tournamentId}
        />
      </div>
      <span style={{ paddingRight: "10px" }}>{rosterName}</span>
      <span style={{ marginTop: "2px" }}>{bestTimeString}</span>
    </CenterFlexRow>
  );
};

const DriverRow = (
  details: {
    driverId: string;
    driverName: string | undefined;
    rosterId: string | undefined;
  },
  stats: DriverTrackStats,
  bestOverall: number | undefined,
  bestMedian: number | undefined,
  overallMedian: number | undefined,
  tournamentId: string
) => {
  const best = stats.best;
  const bestString = !!best ? convertMsToTimeString(best) : "-";
  const bestDiff = !!best && best > 0 && !!bestOverall ? best - bestOverall : 0;
  const bestDiffString =
    bestDiff !== 0 ? convertMsToSecondsString(bestDiff, "+") : "";

  const median = stats.median;
  const medianString = !!median ? convertMsToTimeString(median) : "-";
  const medianDiff =
    !!overallMedian && !!median && median > 0
      ? median - overallMedian
      : undefined;
  const medianDiffString =
    medianDiff !== undefined
      ? convertMsToSecondsString(
          Math.abs(medianDiff),
          medianDiff >= 0 ? "+" : "-"
        )
      : "";

  return (
    <DriverTr key={details.driverId}>
      <td>
        <FlexRow>
          <OrgLogoContainer>
            {!!details.rosterId && (
              <RosterOrgLogo
                rosterId={details.rosterId}
                tournamentId={tournamentId}
              />
            )}
          </OrgLogoContainer>
          <DriverNameContainer>{details.driverName}</DriverNameContainer>
        </FlexRow>
      </td>
      <PlayedCountTd>{stats.roundsPlayed}</PlayedCountTd>
      <BestTd bold={bestDiff === 0}>
        <CenterFlexRow>
          <Time>{bestString}</Time>
          <StatTimeDifferenceContainer>
            <BestStatTimeDifference time={bestDiff}>
              {bestDiff !== 0 ? `${bestDiffString}` : ""}
            </BestStatTimeDifference>
          </StatTimeDifferenceContainer>
        </CenterFlexRow>
      </BestTd>
      <MedianTd bold={bestMedian === median}>
        <CenterFlexRow>
          <Time>{medianString}</Time>
          <StatTimeDifferenceContainer>
            <AverageStatTimeDifference time={medianDiff ?? 0}>
              {medianDiff !== undefined ? `${medianDiffString}` : ""}
            </AverageStatTimeDifference>
          </StatTimeDifferenceContainer>
        </CenterFlexRow>
      </MedianTd>
      <DnfTd>{getDnfIcons(stats.dnfCount)}</DnfTd>
    </DriverTr>
  );
};
